import { CrudFilter, useInfiniteList } from '@refinedev/core';
import { useMemo } from 'react';
import type { CalendarEvent } from 'src/meetings/models';

type CalendarEventsInput = {
  pastOrUpcoming: 'PAST' | 'UPCOMING';
  clientMeetingsOnly?: boolean;
};

export const useCalendarEvents = ({
  pastOrUpcoming,
  clientMeetingsOnly = false,
}: CalendarEventsInput) => {
  const startOfMinute = new Date();
  startOfMinute.setSeconds(0, 0);
  const filters = useMemo(
    () => buildFilters(pastOrUpcoming, startOfMinute, clientMeetingsOnly),
    [pastOrUpcoming, startOfMinute],
  );

  const { data, isLoading, error, refetch, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useInfiniteList<CalendarEvent>({
      resource: 'calendar-events',
      filters,
      pagination: { pageSize: 10 },
    });

  const allPages = (data?.pages ?? []).flatMap(page => page.data);

  return {
    events: allPages,
    isLoading,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  };
};

function buildFilters(
  pastOrUpcoming: 'PAST' | 'UPCOMING',
  date: Date,
  clientMeetingsOnly: boolean,
): CrudFilter[] {
  if (pastOrUpcoming === 'PAST') {
    return [
      {
        field: 'endAt',
        operator: 'lt',
        value: date.toISOString(),
      },
      {
        field: 'order',
        operator: 'eq',
        value: 'desc',
      },
      {
        field: 'clientMeetingsOnly',
        operator: 'eq',
        value: clientMeetingsOnly,
      },
    ];
  }

  return [
    {
      field: 'endAt',
      operator: 'gte',
      value: date.toISOString(),
    },
    {
      field: 'order',
      operator: 'eq',
      value: 'asc',
    },
    {
      field: 'clientMeetingsOnly',
      operator: 'eq',
      value: clientMeetingsOnly,
    },
  ];
}
